import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import {
  SectionTitle,
  DetailsRow,
  InlineIconText,
  FieldGroup,
  FieldLabel,
  StatusLabel,
  FieldValue,
  StatusGroup
} from './LibraryTable.styles';
import { MasterContentRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { StacklineDefaultProfileImage } from 'src/components/SvgIcons/SvgIcons';
import { useBeaconClientId, useCompanyId, useCompanyName } from 'src/utils/Hooks/reduxSelectors';
import { getClientImageUrl } from 'src/utils/image';

interface CurrentContentRowProps {
  currentContent: MasterContentRecord;
}

export const CurrentContentRow: React.FC<CurrentContentRowProps> = ({ currentContent }) => {
  const beaconClientId = useBeaconClientId();
  const companyId = useCompanyId();
  const companyName = useCompanyName();
  const [clientLogoError, setClientLogoError] = useState(false);

  const formatDate = (timestamp?: number, dateString?: string) => {
    if (timestamp) {
      return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    if (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    return 'N/A';
  };

  // Determine if this is a Stackline crawl
  const isStacklineCrawl = currentContent.debug?.updatedBy === 'SearchDataPublishPipeline';

  // Determine if this is from content generation - check source bucket or updatedBy
  const isContentGeneration = 
    currentContent.debug?.source?.locator?.bucket === 'sl-content-generation' ||
    currentContent.debug?.updatedBy === 'content-generation-workflow';

  // Determine the source name - if it's from SearchDataPublishPipeline, show "Stackline Crawl"
  // If it's from content-generation, show the client/company name
  const getSourceName = () => {
    if (isStacklineCrawl) {
      return 'Stackline Crawl';
    }
    if (isContentGeneration) {
      return companyName || 'Your Organization';
    }
    return currentContent.debug?.updatedBy || 'File Upload';
  };

  // Get the client logo URL
  const clientLogoUrl = getClientImageUrl(beaconClientId === -1 ? companyId : beaconClientId);

  return (
    <Box sx={{ marginBottom: '32px' }}>
      <SectionTitle variant="body2">Current Content</SectionTitle>

      <Box sx={{ display: 'flex' }}>
        <DetailsRow>
          <FieldGroup width="109px">
            <FieldLabel variant="body2">Source</FieldLabel>
            <InlineIconText>
              <Box
                sx={{
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  borderRadius: '4px'
                }}
              >
                {isStacklineCrawl || clientLogoError ? (
                <StacklineDefaultProfileImage width="24px" height="24px" />
                ) : (
                  <img
                    src={clientLogoUrl}
                    alt="Organization"
                    style={{
                      width: '24px',
                      height: '24px',
                      objectFit: 'contain'
                    }}
                    onError={() => setClientLogoError(true)}
                  />
                )}
              </Box>
              <FieldValue variant="body2">{getSourceName()}</FieldValue>
            </InlineIconText>
          </FieldGroup>

          <FieldGroup width="85px">
            <FieldLabel variant="body2" sx={{ whiteSpace: 'nowrap' }}>Uploaded Date</FieldLabel>
            <FieldValue variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {formatDate(currentContent.timestamp, currentContent.updatedAt)}
            </FieldValue>
          </FieldGroup>
        </DetailsRow>

        <StatusGroup>
          <StatusLabel variant="body2">Status</StatusLabel>
          <FieldValue variant="body2">Current</FieldValue>
        </StatusGroup>
      </Box>
    </Box>
  );
};

